/* import __COLOCATED_TEMPLATE__ from './selection-modal.hbs.js'; */
var _dec, _class, _descriptor;
function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'transform-class-properties is enabled and runs after the decorators transform.'); }
import { action, computed } from '@ember/object';
import Component from '@ember/component';
import { inject as service } from '@ember/service';
import { isBlank } from '@ember/utils';

/**

 ## Overview

 Modal that allows for selecting a subset of objects from a list.  It provides options
 for "Select All" and "Select None". On closing the modal, it will either return `null`,
 if the user cancelled or clicked outside the modal, or an array of the options marked
 as being selected or not. It will not modify any of its arguments.

 ## Usage

 ```javascript
 modal: Ember.inject.service(),

 actions: {
  editLocations() {
      const options = this.get('allUnits')
        .sortBy('nickname')
        .map(unit => ({
          checked: this.get('employee.units').includes(unit),
          value: unit,
          label: unit.get('nickname')
        }));

      this.get('modal').open('modals/selection-modal', {
        title: t('subunit.edit'),
        options
      })
        .then(savedOptions => {
          if (!Ember.isNone(savedOptions)) {
            this.get('employee.units').setObjects(savedOptions.filterBy('checked').mapBy('value'));
          }
        });
    },
}
```

@param {String} [title] - The title for the modal
@param {String} [subHeading] - The subheading for the modal
@param {String} [subText] - The subtext for the modal
@param {Array} [options] - Array of objects to select from
@param {Boolean} [enableSearch=true] - Adds UI for filtering the options via a search input
@param {String} [searchPlaceholder="Search"] - Optional placeholder text for search input
@param {String} [noResultsText="No results found for ‘{{term}}’"] - Text to display when searching yields no results. A default is used in the template if none is provided.
@param {Boolean} [enableSelectAll=true] - Enables Select All global checkbox
@param {Boolean} [enableIndeterminateState=false] - Enables the indeterminate state on the select all checkbox
@param {String} [selectAllLabel="Select All"] - The label for the Select All checkbox
@param {String} [primaryButtonLabel="Save"] - The label for the primary button
@param {String} [tertiaryColumnLabel] - The label for the tertiary data column

# Option Attributes
@param {Object} [value] - This object will be included with the returned options
@param {String} [label] - The label that will be displayed on the checkbox
@param {String} [labelSecondary] - The secondary label for the checkbox
@param {String} [labelTertiary] - A label that will appear to the far right of the checkbox
@param {Boolean} [isSelected] - Whether the checkbox should start out checked
@param {Boolean} [isDisabled] - Whether the checkbox should be disabled
@param {Boolean} [useLeadingAccessory] - Whether there is a leading accessory
@param {String} [leadingAccessoryImageUrl] - Image url to use for the leading accessory
@param {String} [leadingAccessorySvgName] - Svg name to use for the leading accessory
*/
let SelectionModal = (_dec = computed('initialIsSelectedValues', 'options.@each.checked'), (_class = class SelectionModal extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "intl", _descriptor, this);
    // passed in modalOptions
    _defineProperty(this, "title", null);
    _defineProperty(this, "options", null);
    _defineProperty(this, "enableSearch", true);
    _defineProperty(this, "searchPlaceholder", this.intl.t('common.search.placeholder'));
    _defineProperty(this, "noResultsText", null);
    _defineProperty(this, "enableSelectAll", true);
    _defineProperty(this, "enableIndeterminateState", false);
    _defineProperty(this, "selectAllLabel", this.intl.t('common.selectAll'));
    _defineProperty(this, "primaryButtonLabel", this.intl.t('button.save'));
    _defineProperty(this, "initialIsSelectedValues", null);
    _defineProperty(this, "filterQuery", null);
  }
  get filterProperties() {
    return ['label', 'labelSecondary', 'labelTertiary'];
  }
  get isClean() {
    return this.options.every((option, i) => {
      return option.checked === this.initialIsSelectedValues[i];
    });
  }
  didReceiveAttrs() {
    super.didReceiveAttrs();
    const modalOptions = this.modalOptions;
    const options = modalOptions.options;
    this.setProperties(Object.assign(modalOptions, {
      initialIsSelectedValues: options.map(option => option.checked)
    }));
  }
  save() {
    const initialIsSelectedValues = this.initialIsSelectedValues;
    const results = this.options.map((option, i) => {
      const didChange = option.checked !== initialIsSelectedValues[i];
      return {
        ...option,
        didChange
      };
    });
    this.close(results);
  }
  unlockModalHeightIfQueryEmpty() {
    if (isBlank(this.filterQuery)) {
      this.unlockModalHeight();
    }
  }
}, (_descriptor = _applyDecoratedDescriptor(_class.prototype, "intl", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "filterProperties", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "filterProperties"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isClean", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "isClean"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "save", [action], Object.getOwnPropertyDescriptor(_class.prototype, "save"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "unlockModalHeightIfQueryEmpty", [action], Object.getOwnPropertyDescriptor(_class.prototype, "unlockModalHeightIfQueryEmpty"), _class.prototype)), _class));
export { SelectionModal as default };